/**
 * ESS POS — Stage 4: Stock Keeper Workspace
 *
 * Implements:
 * - Stock Dashboard (Total tracked SKUs, low-stock, out-of-stock, recent movements, received stock)
 * - Product Inventory & Low-Stock Alerts (Live from GET /api/v1/inventory/balances)
 * - Receive Stock (Live POST /api/v1/inventory/receive)
 * - Stock Adjustments & Location Transfers (Live POST /api/v1/inventory/adjust & /transfer)
 * - Damaged Goods Write-Off & Kitchen Stock Usage (Audited movement ledger entries)
 * - Immutable Stock Movement History (Product ID, Tenant ID, Delta, Previous Qty, Resulting Qty, Reason, Employee, Timestamp)
 * - Supplier Records & Recipe-Based Ingredient Deduction Architecture
 */

import { apiClient } from '../api/apiClient';
import { restaurantMockAdapter } from '../api/mockAdapter';
import { openFastSwitchStaffModal, openLockStationModal } from './waiterWorkspace';
import {
  escapeHtml,
  exportRowsToCsv,
  formatCurrency,
  formatDateTime,
  openModal,
  renderEmptyState,
  renderErrorState,
  renderLoadingState,
  showToast,
} from '../components/ui';
import {
  CategoryRecord,
  InventoryBalanceRecord,
  InventoryMovementRecord,
  ProductRecord,
  StockLocationRecord,
} from '../types/contracts';

export async function renderStockKeeperWorkspace(
  viewport: HTMLElement,
  activeTab: string,
  onNavigateTab: (tabId: string) => void
) {
  viewport.innerHTML = renderLoadingState('Loading Inventory Balances & Movement Ledger...');

  try {
    const [locations, categories, products, balances, movements] = await Promise.all([
      apiClient.listStockLocations(),
      apiClient.listCategories(),
      apiClient.listProducts({ forceRefresh: true }),
      apiClient.getInventoryBalances(),
      apiClient.getInventoryMovements(),
    ]);

    const tenantId = apiClient.getEffectiveTenantId();
    const suppliers = restaurantMockAdapter.listSuppliers(tenantId);
    const recipes = restaurantMockAdapter.listRecipes(tenantId);

    const refresh = () => renderStockKeeperWorkspace(viewport, activeTab, onNavigateTab);

    if (activeTab === 'inventory-table') {
      renderProductInventoryScreen(viewport, {
        balances,
        products,
        locations,
        categories,
        onRefresh: refresh,
        onNavigateTab,
      });
    } else if (activeTab === 'receive-stock') {
      renderReceiveStockScreen(viewport, {
        locations,
        products,
        suppliers,
        movements,
        onRefresh: refresh,
      });
    } else if (activeTab === 'adjustments') {
      renderAdjustmentsAndTransfersScreen(viewport, {
        locations,
        products,
        balances,
        onRefresh: refresh,
      });
    } else if (activeTab === 'damaged-usage') {
      renderDamagedAndUsageScreen(viewport, {
        locations,
        products,
        balances,
        onRefresh: refresh,
      });
    } else if (activeTab === 'movements') {
      renderMovementLedgerScreen(viewport, {
        movements,
        tenantId,
      });
    } else if (activeTab === 'suppliers-recipes') {
      renderSuppliersAndRecipesScreen(viewport, {
        tenantId,
        suppliers,
        recipes,
        onRefresh: refresh,
      });
    } else if (activeTab === 'profile') {
      renderStockKeeperProfileScreen(viewport, {
        products,
        balances,
        movements,
        onNavigateTab,
        onRefresh: refresh,
      });
    } else {
      // Default: 'dashboard'
      renderStockDashboard(viewport, {
        products,
        balances,
        movements,
        onNavigateTab,
      });
    }
  } catch (err: any) {
    viewport.innerHTML = renderErrorState({
      title: 'Failed to Load Stock Keeper Workspace',
      message: err?.message || 'Unable to load stock balances or ledger.',
      retryButtonId: 'stock-retry-btn',
    });
    viewport.querySelector('#stock-retry-btn')?.addEventListener('click', () => {
      renderStockKeeperWorkspace(viewport, activeTab, onNavigateTab);
    });
  }
}

// ============================================================================
// 1. STOCK DASHBOARD
// ============================================================================

function renderStockDashboard(
  viewport: HTMLElement,
  params: {
    products: ProductRecord[];
    balances: InventoryBalanceRecord[];
    movements: InventoryMovementRecord[];
    onNavigateTab: (tab: string) => void;
  }
) {
  const outOfStock = params.balances.filter((b) => Number(b.quantity_on_hand) <= 0);
  const lowStock = params.balances.filter(
    (b) => Number(b.quantity_on_hand) > 0 && Number(b.quantity_on_hand) <= Number(b.reorder_threshold)
  );
  const recentReceipts = params.movements.filter(
    (m) => m.reason === 'PURCHASE_RECEIPT' || m.reason === 'INITIAL_STOCK'
  );

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Stock Keeper Inventory Command Center</h2>
          <p class="ess-section-desc">Authoritative stock balances, reorder alerts, goods receipts &amp; immutable movement history</p>
        </div>
        <div class="ess-btn-group">
          <button type="button" class="ess-btn ess-btn-secondary" id="sk-go-adjust">Record Adjustment</button>
          <button type="button" class="ess-btn ess-btn-primary" id="sk-go-receive">+ Receive Stock Delivery</button>
        </div>
      </div>

      <!-- KPI Cards -->
      <div class="ess-kpi-grid">
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Total Tracked Products</div>
          <div class="ess-kpi-value">${params.products.length}</div>
          <div class="ess-kpi-meta">${params.balances.length} active location balance rows</div>
        </div>
        <div class="ess-card ess-kpi-card ${lowStock.length > 0 ? 'ess-kpi-highlight' : ''}">
          <div class="ess-kpi-label">Low-Stock Alerts</div>
          <div class="ess-kpi-value">${lowStock.length}</div>
          <div class="ess-kpi-meta">At or below reorder threshold (5 units)</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Out-of-Stock Items</div>
          <div class="ess-kpi-value" style="color:${outOfStock.length > 0 ? 'var(--ess-danger-text)' : 'inherit'};">${outOfStock.length}</div>
          <div class="ess-kpi-meta">Automatically blocked from POS sale</div>
        </div>
        <div class="ess-card ess-kpi-card">
          <div class="ess-kpi-label">Recorded Movements</div>
          <div class="ess-kpi-value">${params.movements.length}</div>
          <div class="ess-kpi-meta">${recentReceipts.length} stock receipt entries</div>
        </div>
      </div>

      <!-- Low Stock & Out of Stock Attention Panel -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Items Requiring Immediate Attention</h3>
            <span class="ess-badge ess-badge-orange">${lowStock.length + outOfStock.length} Alerts</span>
          </div>
          ${
            lowStock.length + outOfStock.length === 0
              ? renderEmptyState({
                  title: 'All Inventory Levels Healthy',
                  description: 'Every tracked item is above its reorder threshold.',
                })
              : `
            <div class="ess-stack-sm">
              ${[...outOfStock, ...lowStock]
                .map(
                  (item) => `
                <div class="ess-kv-row">
                  <div>
                    <strong>${escapeHtml(item.product_name || item.variant_name)}</strong>
                    <div style="font-size:12px;color:var(--ess-text-muted);">SKU: ${escapeHtml(item.variant_sku)} &bull; ${escapeHtml(item.location_name)}</div>
                  </div>
                  <div style="display:flex;align-items:center;gap:8px;">
                    <span class="ess-badge ${Number(item.quantity_on_hand) <= 0 ? 'ess-badge-danger' : 'ess-badge-warning'}">
                      ${Number(item.quantity_on_hand)} on hand (Min: ${Number(item.reorder_threshold)})
                    </span>
                  </div>
                </div>
              `
                )
                .join('')}
            </div>
          `
          }
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Recent Stock Movements</h3>
            <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="sk-view-ledger">Full Ledger</button>
          </div>
          <div class="ess-stack-sm">
            ${params.movements
              .slice(0, 6)
              .map(
                (m) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(m.product_name)} (${escapeHtml(m.variant_sku)})</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">
                    ${escapeHtml(m.reason)} &bull; By ${escapeHtml(m.user_name)} &bull; ${formatDateTime(m.created_at)}
                  </div>
                </div>
                <div style="text-align:right;">
                  <strong style="color:${Number(m.delta) >= 0 ? 'var(--ess-success-text)' : 'var(--ess-danger-text)'};">
                    ${Number(m.delta) >= 0 ? `+${Number(m.delta)}` : Number(m.delta)}
                  </strong>
                  <div style="font-size:11px;color:var(--ess-text-muted);">Bal: ${Number(m.balance_after)}</div>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#sk-go-receive')?.addEventListener('click', () => params.onNavigateTab('receive-stock'));
  viewport.querySelector('#sk-go-adjust')?.addEventListener('click', () => params.onNavigateTab('adjustments'));
  viewport.querySelector('#sk-view-ledger')?.addEventListener('click', () => params.onNavigateTab('movements'));
}

// ============================================================================
// 2. PRODUCT INVENTORY & LOW-STOCK FILTER TABLE
// ============================================================================

function renderProductInventoryScreen(
  viewport: HTMLElement,
  params: {
    balances: InventoryBalanceRecord[];
    products: ProductRecord[];
    locations: StockLocationRecord[];
    categories: CategoryRecord[];
    onRefresh: () => void;
    onNavigateTab: (tab: string) => void;
  }
) {
  let search = '';
  let statusFilter: 'ALL' | 'LOW' | 'OUT' = 'ALL';

  const renderTable = () => {
    const rows = params.balances.filter((b) => {
      const qty = Number(b.quantity_on_hand);
      if (statusFilter === 'OUT' && qty > 0) return false;
      if (statusFilter === 'LOW' && (qty <= 0 || qty > Number(b.reorder_threshold))) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          (b.product_name || '').toLowerCase().includes(q) ||
          (b.variant_sku || '').toLowerCase().includes(q) ||
          (b.location_name || '').toLowerCase().includes(q)
        );
      }
      return true;
    });

    viewport.innerHTML = `
      <div class="ess-stack-lg">
        <div class="ess-section-header">
          <div>
            <h2 class="ess-section-title">Product Inventory &amp; Stock Balances</h2>
            <p class="ess-section-desc">Inventory quantities cannot be arbitrarily overwritten &mdash; all changes require recorded movements</p>
          </div>
          <div class="ess-btn-group">
            <button type="button" class="ess-btn ess-btn-secondary" id="inv-export-btn">Export Inventory CSV</button>
            <button type="button" class="ess-btn ess-btn-primary" id="inv-new-prod-btn">+ Add Tracked Product</button>
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header" style="flex-wrap:wrap;gap:10px;">
            <input type="search" id="inv-search-input" class="ess-input" placeholder="Search product name, SKU or stock location..." value="${escapeHtml(search)}" style="max-width:320px;" />
            <div class="ess-category-pills">
              <button type="button" class="ess-pill ${statusFilter === 'ALL' ? 'active' : ''}" data-inv-filter="ALL">All Items (${params.balances.length})</button>
              <button type="button" class="ess-pill ${statusFilter === 'LOW' ? 'active' : ''}" data-inv-filter="LOW">Low Stock Alerts</button>
              <button type="button" class="ess-pill ${statusFilter === 'OUT' ? 'active' : ''}" data-inv-filter="OUT">Out of Stock</button>
            </div>
          </div>

          <div class="ess-table-wrap">
            <table class="ess-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product Name</th>
                  <th>Stock Location</th>
                  <th>On-Hand Qty</th>
                  <th>Reorder Min</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Quick Action</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map((b) => {
                    const qty = Number(b.quantity_on_hand);
                    const min = Number(b.reorder_threshold);
                    const isOut = qty <= 0;
                    const isLow = qty > 0 && qty <= min;
                    return `
                      <tr>
                        <td><strong>${escapeHtml(b.variant_sku)}</strong></td>
                        <td>${escapeHtml(b.product_name)}</td>
                        <td>${escapeHtml(b.location_name)}</td>
                        <td><strong>${qty}</strong></td>
                        <td>${min}</td>
                        <td>
                          <span class="ess-badge ${
                            isOut ? 'ess-badge-danger' : isLow ? 'ess-badge-warning' : 'ess-badge-success'
                          }">
                            ${isOut ? 'OUT OF STOCK' : isLow ? 'LOW STOCK' : 'IN STOCK'}
                          </span>
                        </td>
                        <td>${formatDateTime(b.updated_at)}</td>
                        <td>
                          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" data-quick-receive="${escapeHtml(b.variant_id)}">
                            Receive / Adjust
                          </button>
                        </td>
                      </tr>
                    `;
                  })
                  .join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

    const sInput = viewport.querySelector('#inv-search-input') as HTMLInputElement | null;
    sInput?.addEventListener('input', () => {
      search = sInput.value;
      renderTable();
      (viewport.querySelector('#inv-search-input') as HTMLInputElement | null)?.focus();
    });

    viewport.querySelectorAll('[data-inv-filter]').forEach((btn) => {
      btn.addEventListener('click', () => {
        statusFilter = btn.getAttribute('data-inv-filter') as any;
        renderTable();
      });
    });

    viewport.querySelectorAll('[data-quick-receive]').forEach((btn) => {
      btn.addEventListener('click', () => params.onNavigateTab('receive-stock'));
    });

    viewport.querySelector('#inv-export-btn')?.addEventListener('click', () => {
      exportRowsToCsv(
        `ess-inventory-balances-${new Date().toISOString().slice(0, 10)}.csv`,
        ['SKU', 'Product Name', 'Location', 'Quantity On Hand', 'Reorder Threshold', 'Updated At'],
        rows.map((r) => [
          r.variant_sku || '',
          r.product_name || '',
          r.location_name || '',
          Number(r.quantity_on_hand),
          Number(r.reorder_threshold),
          r.updated_at,
        ])
      );
    });

    viewport.querySelector('#inv-new-prod-btn')?.addEventListener('click', () => {
      openModal({
        title: 'Add Tracked Product to Inventory',
        subtitle: 'Creates product, SKU variant, and initial stock ledger movement',
        width: 'md',
        bodyHtml: `
          <form id="sk-add-prod-form" class="ess-form">
            <div class="ess-grid-2">
              <div class="ess-form-group">
                <label class="ess-label">Product Name *</label>
                <input type="text" id="np-name" class="ess-input" placeholder="e.g. Grilled Lamb Chops" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">SKU Code *</label>
                <input type="text" id="np-sku" class="ess-input" placeholder="e.g. GRL-009" required />
              </div>
            </div>
            <div class="ess-grid-2">
              <div class="ess-form-group">
                <label class="ess-label">Category</label>
                <select id="np-cat" class="ess-select">
                  ${params.categories.map((c) => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`).join('')}
                </select>
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Initial Stock Location *</label>
                <select id="np-loc" class="ess-select">
                  ${params.locations.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('')}
                </select>
              </div>
            </div>
            <div class="ess-grid-3">
              <div class="ess-form-group">
                <label class="ess-label">Unit Cost (KES) *</label>
                <input type="number" id="np-cost" class="ess-input" min="0" value="450" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Selling Price (KES) *</label>
                <input type="number" id="np-price" class="ess-input" min="0" value="950" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Opening Qty *</label>
                <input type="number" id="np-qty" class="ess-input" min="0" value="20" required />
              </div>
            </div>
          </form>
        `,
        footerHtml: `
          <button type="button" class="ess-btn ess-btn-secondary" id="np-cancel">Cancel</button>
          <button type="button" class="ess-btn ess-btn-primary" id="np-save">Create Product &amp; Seed Stock</button>
        `,
        onMount: (root, close) => {
          root.querySelector('#np-cancel')?.addEventListener('click', close);
          root.querySelector('#np-save')?.addEventListener('click', async () => {
            const name = (root.querySelector('#np-name') as HTMLInputElement).value.trim();
            const sku = (root.querySelector('#np-sku') as HTMLInputElement).value.trim();
            const categoryId = (root.querySelector('#np-cat') as HTMLSelectElement).value;
            const stockLocationId = (root.querySelector('#np-loc') as HTMLSelectElement).value;
            const costKes = Number((root.querySelector('#np-cost') as HTMLInputElement).value || 0);
            const priceKes = Number((root.querySelector('#np-price') as HTMLInputElement).value || 0);
            const initialStock = Number((root.querySelector('#np-qty') as HTMLInputElement).value || 0);

            if (!name || !sku) {
              showToast({ title: 'Validation Error', message: 'Product name and SKU are required.', type: 'warning' });
              return;
            }
            try {
              await apiClient.createProduct({
                name,
                categoryId: categoryId || undefined,
                variants: [
                  {
                    sku,
                    variantName: 'Standard Portion',
                    costPriceCents: Math.round(costKes * 100),
                    retailPriceCents: Math.round(priceKes * 100),
                    initialStock,
                    stockLocationId,
                  },
                ],
              });
              close();
              showToast({ title: 'Product Created', message: `${name} (${sku}) added to catalog.`, type: 'success' });
              params.onRefresh();
            } catch (err: any) {
              showToast({ title: 'Creation Failed', message: err?.message || 'Error creating product', type: 'danger' });
            }
          });
        },
      });
    });
  };

  renderTable();
}

// ============================================================================
// 3. RECEIVE STOCK SCREEN (POST /api/v1/inventory/receive)
// ============================================================================

function renderReceiveStockScreen(
  viewport: HTMLElement,
  params: {
    locations: StockLocationRecord[];
    products: ProductRecord[];
    suppliers: any[];
    movements: InventoryMovementRecord[];
    onRefresh: () => void;
  }
) {
  const allVariants = params.products.flatMap((p) =>
    p.variants.map((v) => ({ ...v, productName: p.name }))
  );

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Receive Stock Delivery (Goods Receipt Note)</h2>
          <p class="ess-section-desc">Records an immutable PURCHASE_RECEIPT ledger entry and increments on-hand stock atomically</p>
        </div>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Record Incoming Stock Delivery</h3>
            <span class="ess-badge ess-badge-orange">Backend Transaction</span>
          </div>
          <form id="receive-stock-form" class="ess-form">
            <div class="ess-form-group">
              <label class="ess-label">Destination Stock Location *</label>
              <select id="rcv-location" class="ess-select" required>
                ${params.locations.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)} (${escapeHtml(l.type)})</option>`).join('')}
              </select>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Product / SKU Variant *</label>
              <select id="rcv-variant" class="ess-select" required>
                ${allVariants
                  .map(
                    (v) => `
                  <option value="${escapeHtml(v.id)}">
                    ${escapeHtml(v.sku)} &mdash; ${escapeHtml(v.productName)} (Current: ${Number(v.total_stock ?? 0)})
                  </option>
                `
                  )
                  .join('')}
              </select>
            </div>
            <div class="ess-grid-2">
              <div class="ess-form-group">
                <label class="ess-label">Quantity Received *</label>
                <input type="number" id="rcv-qty" class="ess-input" min="1" value="15" required />
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Updated Unit Cost (KES)</label>
                <input type="number" id="rcv-cost" class="ess-input" min="0" placeholder="Optional cost update" />
              </div>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Supplier &amp; Delivery Reference / Notes *</label>
              <input type="text" id="rcv-notes" class="ess-input" value="GRN-2026-109 • Highlands Prime Meat Suppliers" required />
            </div>
            <button type="submit" class="ess-btn ess-btn-primary ess-btn-lg ess-btn-block" id="rcv-submit-btn">
              Confirm Goods Receipt &amp; Update Stock Balance
            </button>
          </form>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Recently Received Stock</h3>
          </div>
          <div class="ess-stack-sm">
            ${params.movements
              .filter((m) => m.reason === 'PURCHASE_RECEIPT' || m.reason === 'INITIAL_STOCK')
              .slice(0, 6)
              .map(
                (m) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(m.product_name)} (${escapeHtml(m.variant_sku)})</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">
                    ${escapeHtml(m.notes || m.reason)} &bull; ${formatDateTime(m.created_at)}
                  </div>
                </div>
                <div style="text-align:right;">
                  <span class="ess-badge ess-badge-success">+${Number(m.delta)} Received</span>
                  <div style="font-size:11px;color:var(--ess-text-muted);">New Bal: ${Number(m.balance_after)}</div>
                </div>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  const form = viewport.querySelector('#receive-stock-form') as HTMLFormElement | null;
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const stockLocationId = (viewport.querySelector('#rcv-location') as HTMLSelectElement).value;
    const variantId = (viewport.querySelector('#rcv-variant') as HTMLSelectElement).value;
    const quantity = Number((viewport.querySelector('#rcv-qty') as HTMLInputElement).value || 0);
    const costRaw = (viewport.querySelector('#rcv-cost') as HTMLInputElement).value;
    const notes = (viewport.querySelector('#rcv-notes') as HTMLInputElement).value.trim();

    if (quantity <= 0) {
      showToast({ title: 'Invalid Quantity', message: 'Received quantity must be greater than zero.', type: 'warning' });
      return;
    }

    const btn = viewport.querySelector('#rcv-submit-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Recording Goods Receipt...';

    try {
      await apiClient.receiveStock({
        stockLocationId,
        items: [
          {
            variantId,
            quantity,
            costPriceCents: costRaw ? Math.round(Number(costRaw) * 100) : undefined,
          },
        ],
        notes,
      });
      showToast({
        title: 'Stock Received Successfully',
        message: `Added +${quantity} units to inventory ledger.`,
        type: 'success',
      });
      params.onRefresh();
    } catch (err: any) {
      showToast({ title: 'Receipt Failed', message: err?.message || 'Error receiving stock', type: 'danger' });
      btn.disabled = false;
      btn.textContent = 'Confirm Goods Receipt & Update Stock Balance';
    }
  });
}

// ============================================================================
// 4. STOCK ADJUSTMENTS & LOCATION TRANSFERS
// ============================================================================

function renderAdjustmentsAndTransfersScreen(
  viewport: HTMLElement,
  params: {
    locations: StockLocationRecord[];
    products: ProductRecord[];
    balances: InventoryBalanceRecord[];
    onRefresh: () => void;
  }
) {
  const allVariants = params.products.flatMap((p) =>
    p.variants.map((v) => ({ ...v, productName: p.name }))
  );

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Audited Stock Adjustments &amp; Inter-Location Transfers</h2>
          <p class="ess-section-desc">Every adjustment records Product ID, Tenant ID, Previous Qty, Delta, Resulting Qty, Reason, Employee &amp; Timestamp</p>
        </div>
      </div>

      <div class="ess-grid-2">
        <!-- Manual Stock Count Adjustment -->
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Physical Stocktake Adjustment</h3>
            <span class="ess-badge ess-badge-orange">Audited Ledger</span>
          </div>
          <form id="adjust-stock-form" class="ess-form">
            <div class="ess-form-group">
              <label class="ess-label">Stock Location *</label>
              <select id="adj-location" class="ess-select" required>
                ${params.locations.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('')}
              </select>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Product Variant *</label>
              <select id="adj-variant" class="ess-select" required>
                ${allVariants
                  .map(
                    (v) => `
                  <option value="${escapeHtml(v.id)}">
                    ${escapeHtml(v.sku)} &mdash; ${escapeHtml(v.productName)} (Current: ${Number(v.total_stock ?? 0)})
                  </option>
                `
                  )
                  .join('')}
              </select>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Verified Resulting Quantity On Hand *</label>
              <input type="number" id="adj-new-qty" class="ess-input" min="0" value="20" required />
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Mandatory Adjustment Explanation (Min 5 chars) *</label>
              <textarea id="adj-notes" class="ess-textarea" rows="2" placeholder="e.g. Weekly physical stock count reconciliation by Peter Njoroge" required></textarea>
            </div>
            <button type="submit" class="ess-btn ess-btn-primary ess-btn-block" id="adj-submit-btn">
              Record Audited Stock Adjustment
            </button>
          </form>
        </div>

        <!-- Inter-Location Stock Transfer -->
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Transfer Stock Between Locations</h3>
            <span class="ess-badge ess-badge-neutral">TRANSFER_OUT / IN</span>
          </div>
          <form id="transfer-stock-form" class="ess-form">
            <div class="ess-grid-2">
              <div class="ess-form-group">
                <label class="ess-label">Source Location *</label>
                <select id="trf-from" class="ess-select">
                  ${params.locations.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('')}
                </select>
              </div>
              <div class="ess-form-group">
                <label class="ess-label">Destination Location *</label>
                <select id="trf-to" class="ess-select">
                  ${params.locations
                    .map((l, i) => `<option value="${escapeHtml(l.id)}" ${i === 1 ? 'selected' : ''}>${escapeHtml(l.name)}</option>`)
                    .join('')}
                </select>
              </div>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Product Variant *</label>
              <select id="trf-variant" class="ess-select">
                ${allVariants.map((v) => `<option value="${escapeHtml(v.id)}">${escapeHtml(v.sku)} &mdash; ${escapeHtml(v.productName)}</option>`).join('')}
              </select>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Transfer Quantity *</label>
              <input type="number" id="trf-qty" class="ess-input" min="1" value="5" required />
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Transfer Notes</label>
              <input type="text" id="trf-notes" class="ess-input" value="Kitchen prep requisition" />
            </div>
            <button type="submit" class="ess-btn ess-btn-secondary ess-btn-block" id="trf-submit-btn">
              Execute Atomic Location Transfer
            </button>
          </form>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#adjust-stock-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const stockLocationId = (viewport.querySelector('#adj-location') as HTMLSelectElement).value;
    const variantId = (viewport.querySelector('#adj-variant') as HTMLSelectElement).value;
    const newQuantity = Number((viewport.querySelector('#adj-new-qty') as HTMLInputElement).value || 0);
    const notes = (viewport.querySelector('#adj-notes') as HTMLTextAreaElement).value.trim();

    if (notes.length < 5) {
      showToast({ title: 'Explanation Required', message: 'Please enter at least 5 characters explaining the adjustment.', type: 'warning' });
      return;
    }

    const btn = viewport.querySelector('#adj-submit-btn') as HTMLButtonElement;
    btn.disabled = true;
    try {
      await apiClient.adjustStock({
        stockLocationId,
        variantId,
        newQuantity,
        reason: 'ADJUSTMENT',
        notes,
      });
      showToast({ title: 'Stock Adjustment Recorded', message: `Resulting quantity: ${newQuantity}`, type: 'success' });
      params.onRefresh();
    } catch (err: any) {
      showToast({ title: 'Adjustment Failed', message: err?.message || 'Error adjusting stock', type: 'danger' });
      btn.disabled = false;
    }
  });

  viewport.querySelector('#transfer-stock-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fromLocationId = (viewport.querySelector('#trf-from') as HTMLSelectElement).value;
    const toLocationId = (viewport.querySelector('#trf-to') as HTMLSelectElement).value;
    const variantId = (viewport.querySelector('#trf-variant') as HTMLSelectElement).value;
    const quantity = Number((viewport.querySelector('#trf-qty') as HTMLInputElement).value || 0);
    const notes = (viewport.querySelector('#trf-notes') as HTMLInputElement).value.trim();

    const btn = viewport.querySelector('#trf-submit-btn') as HTMLButtonElement;
    btn.disabled = true;
    try {
      await apiClient.transferStock({
        fromLocationId,
        toLocationId,
        variantId,
        quantity,
        notes,
      });
      showToast({ title: 'Stock Transferred', message: `Moved ${quantity} units safely.`, type: 'success' });
      params.onRefresh();
    } catch (err: any) {
      showToast({ title: 'Transfer Failed', message: err?.message || 'Error transferring stock', type: 'danger' });
      btn.disabled = false;
    }
  });
}

// ============================================================================
// 5. DAMAGED GOODS & KITCHEN STOCK USAGE
// ============================================================================

function renderDamagedAndUsageScreen(
  viewport: HTMLElement,
  params: {
    locations: StockLocationRecord[];
    products: ProductRecord[];
    balances: InventoryBalanceRecord[];
    onRefresh: () => void;
  }
) {
  const allVariants = params.products.flatMap((p) =>
    p.variants.map((v) => ({ ...v, productName: p.name }))
  );

  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Damaged Goods Write-Off &amp; Kitchen Stock Usage</h2>
          <p class="ess-section-desc">Record spoilage, breakage, or direct kitchen preparation consumption into the movement ledger</p>
        </div>
      </div>

      <div class="ess-card" style="max-width:640px;">
        <div class="ess-card-header">
          <h3 class="ess-card-title">Log Damaged / Spoiled Goods or Kitchen Usage</h3>
          <span class="ess-badge ess-badge-danger">DAMAGE_WRITEOFF</span>
        </div>
        <form id="damage-form" class="ess-form">
          <div class="ess-grid-2">
            <div class="ess-form-group">
              <label class="ess-label">Stock Location *</label>
              <select id="dmg-loc" class="ess-select">
                ${params.locations.map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)}</option>`).join('')}
              </select>
            </div>
            <div class="ess-form-group">
              <label class="ess-label">Operation Type *</label>
              <select id="dmg-type" class="ess-select">
                <option value="DAMAGE_WRITEOFF">Damaged / Spoiled Write-Off</option>
                <option value="ADJUSTMENT">Direct Kitchen Usage / Prep Deduction</option>
              </select>
            </div>
          </div>

          <div class="ess-form-group">
            <label class="ess-label">Product Variant *</label>
            <select id="dmg-variant" class="ess-select">
              ${allVariants
                .map(
                  (v) => `
                <option value="${escapeHtml(v.id)}" data-cur-stock="${Number(v.total_stock ?? 0)}">
                  ${escapeHtml(v.sku)} &mdash; ${escapeHtml(v.productName)} (Available: ${Number(v.total_stock ?? 0)})
                </option>
              `
                )
                .join('')}
            </select>
          </div>

          <div class="ess-form-group">
            <label class="ess-label">Quantity Deducted (Units) *</label>
            <input type="number" id="dmg-qty" class="ess-input" min="1" value="1" required />
          </div>

          <div class="ess-form-group">
            <label class="ess-label">Mandatory Incident / Usage Notes *</label>
            <textarea id="dmg-notes" class="ess-textarea" rows="2" placeholder="e.g. 1 portion spoiled during cold-room defrost inspection" required></textarea>
          </div>

          <button type="submit" class="ess-btn ess-btn-danger ess-btn-block" id="dmg-submit">
            Record Deduction in Stock Movement Ledger
          </button>
        </form>
      </div>
    </div>
  `;

  viewport.querySelector('#damage-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const stockLocationId = (viewport.querySelector('#dmg-loc') as HTMLSelectElement).value;
    const reason = (viewport.querySelector('#dmg-type') as HTMLSelectElement).value as
      | 'DAMAGE_WRITEOFF'
      | 'ADJUSTMENT';
    const variantSelect = viewport.querySelector('#dmg-variant') as HTMLSelectElement;
    const variantId = variantSelect.value;
    const deductQty = Number((viewport.querySelector('#dmg-qty') as HTMLInputElement).value || 0);
    const notes = (viewport.querySelector('#dmg-notes') as HTMLTextAreaElement).value.trim();

    const bal = params.balances.find(
      (b) => b.stock_location_id === stockLocationId && b.variant_id === variantId
    );
    const currentQty = bal ? Number(bal.quantity_on_hand) : 0;
    const newQuantity = Math.max(0, currentQty - deductQty);

    if (notes.length < 5) {
      showToast({ title: 'Reason Required', message: 'Please provide a detailed explanation (min 5 chars).', type: 'warning' });
      return;
    }

    try {
      await apiClient.adjustStock({
        stockLocationId,
        variantId,
        newQuantity,
        reason,
        notes: `${reason === 'DAMAGE_WRITEOFF' ? '[DAMAGED]' : '[KITCHEN USAGE]'} Deducted ${deductQty} units: ${notes}`,
      });
      showToast({
        title: 'Deduction Logged',
        message: `Previous: ${currentQty} -> Resulting: ${newQuantity}`,
        type: 'success',
      });
      params.onRefresh();
    } catch (err: any) {
      showToast({ title: 'Operation Failed', message: err?.message || 'Error recording deduction', type: 'danger' });
    }
  });
}

// ============================================================================
// 6. IMMUTABLE STOCK MOVEMENT HISTORY LEDGER
// ============================================================================

function renderMovementLedgerScreen(
  viewport: HTMLElement,
  params: {
    movements: InventoryMovementRecord[];
    tenantId: string;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Immutable Stock Movement History</h2>
          <p class="ess-section-desc">Full audit trail: Product ID, Tenant ID, Previous Qty, Delta, Resulting Qty, Reason, Employee &amp; Timestamp</p>
        </div>
        <button type="button" class="ess-btn ess-btn-secondary" id="mov-export-csv">Export Ledger CSV</button>
      </div>

      <div class="ess-card">
        <div class="ess-table-wrap">
          <table class="ess-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Product &amp; SKU</th>
                <th>Location</th>
                <th>Reason</th>
                <th>Prev Qty</th>
                <th>Changed (Delta)</th>
                <th>Resulting Qty</th>
                <th>Employee</th>
                <th>Notes / Ref</th>
              </tr>
            </thead>
            <tbody>
              ${params.movements
                .map(
                  (m) => `
                <tr>
                  <td>${formatDateTime(m.created_at)}</td>
                  <td>
                    <strong>${escapeHtml(m.product_name)}</strong>
                    <div style="font-size:11px;color:var(--ess-text-muted);">SKU: ${escapeHtml(m.variant_sku)} &bull; Tenant: ${escapeHtml(m.tenant_id.slice(0, 8))}</div>
                  </td>
                  <td>${escapeHtml(m.location_name)}</td>
                  <td><span class="ess-badge ess-badge-neutral">${escapeHtml(m.reason)}</span></td>
                  <td>${Number(m.previous_quantity ?? Number(m.balance_after) - Number(m.delta))}</td>
                  <td>
                    <strong style="color:${Number(m.delta) >= 0 ? 'var(--ess-success-text)' : 'var(--ess-danger-text)'};">
                      ${Number(m.delta) >= 0 ? `+${Number(m.delta)}` : Number(m.delta)}
                    </strong>
                  </td>
                  <td><strong>${Number(m.balance_after)}</strong></td>
                  <td>${escapeHtml(m.user_name || 'Authorized Staff')}</td>
                  <td>${escapeHtml(m.notes || m.reference_id || '—')}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#mov-export-csv')?.addEventListener('click', () => {
    exportRowsToCsv(
      `ess-stock-movements-${new Date().toISOString().slice(0, 10)}.csv`,
      ['Timestamp', 'Tenant ID', 'Product', 'SKU', 'Location', 'Reason', 'Previous Qty', 'Delta', 'Resulting Qty', 'Employee', 'Notes'],
      params.movements.map((m) => [
        m.created_at,
        m.tenant_id,
        m.product_name || '',
        m.variant_sku || '',
        m.location_name || '',
        m.reason,
        Number(m.previous_quantity ?? Number(m.balance_after) - Number(m.delta)),
        Number(m.delta),
        Number(m.balance_after),
        m.user_name || 'Staff',
        m.notes || '',
      ])
    );
  });
}

// ============================================================================
// 7. SUPPLIERS & RECIPE-BASED INGREDIENT DEDUCTION ARCHITECTURE
// ============================================================================

function renderSuppliersAndRecipesScreen(
  viewport: HTMLElement,
  params: {
    tenantId: string;
    suppliers: any[];
    recipes: any[];
    onRefresh: () => void;
  }
) {
  viewport.innerHTML = `
    <div class="ess-stack-lg">
      <div class="ess-section-header">
        <div>
          <h2 class="ess-section-title">Supplier Directory &amp; Recipe Ingredient Deduction Mapping</h2>
          <p class="ess-section-desc">Supports composite menu items (e.g. selling a Burger deducts buns, beef patty, cheddar &amp; sauce)</p>
        </div>
        <button type="button" class="ess-btn ess-btn-primary" id="sup-add-btn">+ Add Supplier</button>
      </div>

      <div class="ess-grid-2">
        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Approved Restaurant Suppliers</h3>
            <span class="ess-badge ess-badge-neutral">${params.suppliers.length} Active</span>
          </div>
          <div class="ess-stack-sm">
            ${params.suppliers
              .map(
                (s) => `
              <div class="ess-kv-row">
                <div>
                  <strong>${escapeHtml(s.name)}</strong>
                  <div style="font-size:12px;color:var(--ess-text-muted);">${escapeHtml(s.category)} &bull; Contact: ${escapeHtml(s.contactPerson)} (${escapeHtml(s.phone)})</div>
                </div>
                <span class="ess-badge ess-badge-success">${s.leadTimeDays}d Lead</span>
              </div>
            `
              )
              .join('')}
          </div>
        </div>

        <div class="ess-card">
          <div class="ess-card-header">
            <h3 class="ess-card-title">Recipe-Based Ingredient Deduction (Bill of Materials)</h3>
            <span class="ess-badge ess-badge-orange">Recipe Architecture</span>
          </div>
          <div class="ess-stack-md">
            ${params.recipes
              .map(
                (r) => `
              <div style="border:1px solid var(--ess-border);border-radius:8px;padding:12px;">
                <div style="display:flex;justify-content:space-between;margin-bottom:8px;">
                  <strong>${escapeHtml(r.menuProductName)}</strong>
                  <span class="ess-badge ess-badge-neutral">SKU: ${escapeHtml(r.menuSku)}</span>
                </div>
                ${r.ingredients
                  .map(
                    (ing: any) => `
                  <div class="ess-kv-row" style="font-size:13px;">
                    <span>&bull; ${escapeHtml(ing.ingredientName)}</span>
                    <span><strong>${ing.quantityPerPortion} ${escapeHtml(ing.unit)}</strong> (${formatCurrency(ing.estimatedCostCents)})</span>
                  </div>
                `
                  )
                  .join('')}
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  viewport.querySelector('#sup-add-btn')?.addEventListener('click', () => {
    openModal({
      title: 'Register Approved Supplier',
      width: 'sm',
      bodyHtml: `
        <div class="ess-form">
          <div class="ess-form-group"><label class="ess-label">Supplier Company Name *</label><input type="text" id="sup-name" class="ess-input" required /></div>
          <div class="ess-form-group"><label class="ess-label">Supply Category *</label><input type="text" id="sup-cat" class="ess-input" placeholder="e.g. Beverages & Dairy" required /></div>
          <div class="ess-form-group"><label class="ess-label">Contact Person *</label><input type="text" id="sup-person" class="ess-input" required /></div>
          <div class="ess-form-group"><label class="ess-label">Phone Number *</label><input type="tel" id="sup-phone" class="ess-input" required /></div>
        </div>
      `,
      footerHtml: `
        <button type="button" class="ess-btn ess-btn-secondary" id="sup-cancel">Cancel</button>
        <button type="button" class="ess-btn ess-btn-primary" id="sup-save">Save Supplier</button>
      `,
      onMount: (root, close) => {
        root.querySelector('#sup-cancel')?.addEventListener('click', close);
        root.querySelector('#sup-save')?.addEventListener('click', () => {
          const name = (root.querySelector('#sup-name') as HTMLInputElement).value.trim();
          const category = (root.querySelector('#sup-cat') as HTMLInputElement).value.trim();
          const contactPerson = (root.querySelector('#sup-person') as HTMLInputElement).value.trim();
          const phone = (root.querySelector('#sup-phone') as HTMLInputElement).value.trim();
          if (!name || !contactPerson) return;
          restaurantMockAdapter.addSupplier(params.tenantId, {
            name,
            category: category || 'General Goods',
            contactPerson,
            phone,
            email: 'orders@supplier.co.ke',
            leadTimeDays: 1,
          });
          close();
          showToast({ title: 'Supplier Added', message: name, type: 'success' });
          params.onRefresh();
        });
      },
    });
  });
}

// ============================================================================
// 8. PRIVATE STOCK KEEPER PROFILE & PIN POWER
// ============================================================================

function renderStockKeeperProfileScreen(
  viewport: HTMLElement,
  params: {
    products: any[];
    balances: InventoryBalanceRecord[];
    movements: InventoryMovementRecord[];
    onNavigateTab: (tab: string) => void;
    onRefresh: () => void;
  }
) {
  const user = apiClient.getUser();
  const tenant = apiClient.getTenant();
  const tenantId = apiClient.getEffectiveTenantId();
  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Stock Keeper';

  const receivesCount = params.movements.filter((m) => m.reason === 'PURCHASE_RECEIPT').length;
  const adjustmentsCount = params.movements.filter((m) => m.reason === 'CYCLE_COUNT_ADJUSTMENT' || m.reason === 'DAMAGE_WRITEOFF').length;

  viewport.innerHTML = `
    <div class="ess-stack-md">
      <div class="ess-card">
        <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:16px;">
          <div style="display:flex;align-items:center;gap:16px;">
            <div style="width:68px;height:68px;border-radius:50%;background:var(--ess-warning,#d97706);color:#fff;display:flex;align-items:center;justify-content:center;font-size:26px;font-weight:700;">
              ${escapeHtml((user?.firstName?.[0] || 'S') + (user?.lastName?.[0] || 'K'))}
            </div>
            <div>
              <div style="display:flex;align-items:center;gap:8px;">
                <h2 style="margin:0;font-size:22px;font-weight:800;color:var(--ess-text-primary);">${escapeHtml(fullName)}</h2>
                <span class="ess-role-pill" style="font-size:11px;">STOCK KEEPER</span>
              </div>
              <div style="font-size:13px;color:var(--ess-text-secondary);margin-top:2px;">
                ${escapeHtml(user?.email || '')} &bull; ${escapeHtml(tenant?.name || 'Restaurant Inventory')}
              </div>
              <div style="font-size:12px;color:var(--ess-success-text);margin-top:4px;font-weight:600;">
                ● Private Session Protected with 4-Digit Fast Login PIN
              </div>
            </div>
          </div>

          <div style="display:flex;gap:10px;">
            <button type="button" class="ess-btn ess-btn-secondary" id="skp-lock-btn" style="display:flex;align-items:center;gap:6px;font-weight:700;">
              🔒 Lock Station Now
            </button>
            <button type="button" class="ess-btn ess-btn-primary" id="skp-switch-btn" style="display:flex;align-items:center;gap:6px;font-weight:700;">
              👤 Switch Staff
            </button>
          </div>
        </div>
      </div>

      <!-- Inventory Metrics Grid -->
      <div class="ess-grid-4">
        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Tracked SKUs</div>
          <div class="ess-metric-value">${params.balances.length}</div>
          <div class="ess-metric-sub">Across all storage locations</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Receipt Batches</div>
          <div class="ess-metric-value" style="color:var(--ess-primary);">${receivesCount}</div>
          <div class="ess-metric-sub">Received goods recorded</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Stock Adjustments</div>
          <div class="ess-metric-value">${adjustmentsCount}</div>
          <div class="ess-metric-sub">Damage write-offs & audits</div>
        </div>

        <div class="ess-card ess-metric-card">
          <div class="ess-metric-label">Movement Ledger</div>
          <div class="ess-metric-value">${params.movements.length}</div>
          <div class="ess-metric-sub">Total stock transactions</div>
        </div>
      </div>

      <!-- PIN Management & Security -->
      <div class="ess-grid-2">
        <div class="ess-card">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:700;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            🔑 My Secret Fast-Login PIN
          </h3>
          <p style="font-size:13px;color:var(--ess-text-secondary);margin:0 0 16px 0;">
            Your personal 4 to 6-digit numeric PIN protects your inventory receiving and stock audit records.
          </p>

          <form id="skp-pin-form" style="display:flex;flex-direction:column;gap:12px;">
            <div class="ess-form-group">
              <label class="ess-label">New 4 to 6-Digit PIN *</label>
              <input type="password" id="skp-new-pin" class="ess-input" maxlength="6" inputmode="numeric" placeholder="e.g. 7412" style="font-size:18px;letter-spacing:6px;width:100%;max-width:260px;" required />
            </div>

            <div class="ess-form-group">
              <label class="ess-label">Confirm New PIN *</label>
              <input type="password" id="skp-confirm-pin" class="ess-input" maxlength="6" inputmode="numeric" placeholder="Re-enter PIN" style="font-size:18px;letter-spacing:6px;width:100%;max-width:260px;" required />
            </div>

            <div id="skp-pin-error" style="color:var(--ess-danger,#ef4444);font-size:12px;font-weight:600;min-height:16px;"></div>

            <div>
              <button type="submit" class="ess-btn ess-btn-primary" id="skp-save-pin-btn" style="font-weight:700;">
                Save My Secret PIN
              </button>
            </div>
          </form>
        </div>

        <div class="ess-card">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:700;color:var(--ess-text-primary);display:flex;align-items:center;gap:6px;">
            🛡️ Role Privacy &amp; Station Isolation
          </h3>
          <p style="font-size:13px;color:var(--ess-text-secondary);margin:0 0 14px 0;">
            ESS POS enforces server-level role and user isolation:
          </p>
          <ul style="margin:0;padding-left:18px;font-size:13px;color:var(--ess-text-primary);line-height:1.6;">
            <li><strong>Strict Role Boundary:</strong> Waiters and cashiers cannot access or modify warehouse inventory balances or receipt ledgers.</li>
            <li><strong>Stock Keeper Isolation:</strong> Stock keepers cannot open cashier drawers or view floor table billing.</li>
            <li><strong>PIN Verification:</strong> Handover or switching to another employee always requires entering their PIN.</li>
          </ul>

          <div style="margin-top:20px;padding:12px;background:var(--ess-bg-subtle,#f8fafc);border-radius:6px;border:1px solid var(--ess-border);">
            <div style="font-size:12px;font-weight:700;color:var(--ess-text-primary);">Quick Actions:</div>
            <div style="display:flex;gap:8px;margin-top:8px;">
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="skp-go-inv">Product Inventory</button>
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="skp-go-rec">Receive Stock</button>
              <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="skp-go-mov">Movement Ledger</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Bind actions
  viewport.querySelector('#skp-lock-btn')?.addEventListener('click', () => {
    openLockStationModal(user, tenantId);
  });

  viewport.querySelector('#skp-switch-btn')?.addEventListener('click', () => {
    openFastSwitchStaffModal(tenantId);
  });

  viewport.querySelector('#skp-go-inv')?.addEventListener('click', () => params.onNavigateTab('inventory-table'));
  viewport.querySelector('#skp-go-rec')?.addEventListener('click', () => params.onNavigateTab('receive-stock'));
  viewport.querySelector('#skp-go-mov')?.addEventListener('click', () => params.onNavigateTab('movements'));

  // Bind PIN change form
  const pinForm = viewport.querySelector('#skp-pin-form') as HTMLFormElement;
  pinForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const newPin = (viewport.querySelector('#skp-new-pin') as HTMLInputElement)?.value.trim();
    const confirmPin = (viewport.querySelector('#skp-confirm-pin') as HTMLInputElement)?.value.trim();
    const errDiv = viewport.querySelector('#skp-pin-error') as HTMLElement;
    const saveBtn = viewport.querySelector('#skp-save-pin-btn') as HTMLButtonElement;

    if (!newPin || newPin.length < 4 || newPin.length > 6 || !/^\d+$/.test(newPin)) {
      if (errDiv) errDiv.textContent = 'PIN must be 4 to 6 numeric digits';
      return;
    }
    if (newPin !== confirmPin) {
      if (errDiv) errDiv.textContent = 'PINs do not match. Please verify.';
      return;
    }

    if (errDiv) errDiv.textContent = '';
    if (saveBtn) {
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving PIN...';
    }

    try {
      await apiClient.updateStaffPin(newPin);
      showToast({
        title: 'Secret PIN Updated',
        message: 'Your personal fast-login PIN is now active for this station.',
        type: 'success',
      });
      (viewport.querySelector('#skp-new-pin') as HTMLInputElement).value = '';
      (viewport.querySelector('#skp-confirm-pin') as HTMLInputElement).value = '';
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save My Secret PIN';
      }
    } catch (err: any) {
      if (errDiv) errDiv.textContent = err?.message || 'Failed to update PIN.';
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save My Secret PIN';
      }
    }
  });
}


